import {
  ActionTypes as FeedItemActionTypes,
  IArchiveLeadSuccessAction,
} from '@app/components/FeedItem/modules/feedItems'
import { Contact, IContact } from '@app/models/Contact'
import {
  EarningSummaryItem,
  IEarningSummaryItem,
} from '@app/models/EarningSummaryItem'
import {
  EntityPermissions,
  IEntityPermissions,
} from '@app/models/EntityPermissions'
import { ApplicationState } from '@app/store'
import { errorToast } from '@app/utils/notifications'
import {
  IRequestOptions,
  makeAuthenticatedRequest,
  RequestMethod,
} from '@app/utils/request'
import { List, Record } from 'immutable'
import { push } from 'connected-react-router'
import { Action, Reducer } from 'redux'
import { fork, put, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------

export enum ConnectionViewScope {
  Activity = 'Activity',
  Details = 'Details',
  Properties = 'Properties',
  Connections = 'Connections',
  Leads = 'Leads',
  Earnings = 'Earnings',
}

export interface IConnectionViewState {
  readonly earnings: List<IEarningSummaryItem>
  readonly error: string
  readonly isDeleting: boolean
  readonly isLoading: boolean
  readonly item: IContact
  readonly permissions: IEntityPermissions
  readonly scope: ConnectionViewScope
  readonly showConfirmDelete: boolean
}

const ConnectionViewRecord = Record({
  earnings: List<EarningSummaryItem>(),
  error: '',
  isDeleting: false,
  isLoading: false,
  item: new Contact(),
  permissions: new EntityPermissions(),
  scope: ConnectionViewScope.Activity,
  showConfirmDelete: false,
})

export class ConnectionViewState extends ConnectionViewRecord
  implements IConnectionViewState {
  public static fromJS(initialConnectionView?: Partial<IConnectionViewState>) {
    return new ConnectionViewState(initialConnectionView)
  }

  public readonly earnings: List<EarningSummaryItem>
  public readonly error: string
  public readonly isDeleting: boolean
  public readonly isLoading: boolean
  public readonly item: Contact
  public readonly permissions: EntityPermissions
  public readonly scope: ConnectionViewScope
  public readonly showConfirmDelete: boolean

  constructor(props?: Partial<IConnectionViewState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum ActionTypes {
  DeleteConnection = 'CONNECTION_VIEW::DELETE',
  DeleteConnectionFailure = 'CONNECTION_VIEW::DELETE_FAILURE',
  DeleteConnectionRequest = 'CONNECTION_VIEW::DELETE_REQUEST',
  DeleteConnectionSuccess = 'CONNECTION_VIEW::DELETE_SUCCESS',
  LoadConnection = 'CONNECTION_VIEW::LOAD_CONNECTION',
  LoadConnectionRequest = 'CONNECTION_VIEW::LOAD_CONNECTION_REQUEST',
  LoadConnectionSuccess = 'CONNECTION_VIEW::LOAD_CONNECTION_SUCCESS',
  LoadConnectionFailure = 'CONNECTION_VIEW::LOAD_CONNECTION_FAILURE',
  ResetConnectionView = 'CONNECTION_VIEW::RESET_STATE',
  SetScope = 'CONNECTION_VIEW::SET_SCOPE',
  ToggleConfirmDelete = 'CONNECTION_VIEW::TOGGLE_CONFIRM_DELETE',
  ToggleFavorite = 'CONNECTION_VIEW::TOGGLE_FAVORITE',
  ToggleFavoriteSuccess = 'CONNECTION_VIEW::TOGGLE_FAVORITE_SUCCESS',
}

interface IDeleteConnectionAction {
  connectionId: string
  type: ActionTypes.DeleteConnection
}

interface IDeleteConnectionFailureAction {
  error: string
  type: ActionTypes.DeleteConnectionFailure
}

interface IDeleteConnectionRequestAction {
  type: ActionTypes.DeleteConnectionRequest
}

interface IDeleteConnectionSuccessAction {
  type: ActionTypes.DeleteConnectionSuccess
}

interface ILoadConnectionFailureAction {
  error: string
  type: ActionTypes.LoadConnectionFailure
}

interface ILoadConnectionRequestAction {
  type: ActionTypes.LoadConnectionRequest
}

interface ILoadConnectionSuccessAction {
  earnings: EarningSummaryItem[]
  item: Contact
  permissions: EntityPermissions
  type: ActionTypes.LoadConnectionSuccess
}

interface ILoadConnectionViewAction {
  type: ActionTypes.LoadConnection
  value: string
}

interface IResetConnectionViewAction {
  type: ActionTypes.ResetConnectionView
}

interface ISetScopeAction {
  scope: ConnectionViewScope
  type: ActionTypes.SetScope
}

interface IToggleConfirmDeleteAction {
  show: boolean
  type: ActionTypes.ToggleConfirmDelete
}

interface IToggleFavoriteAction {
  isFavorite: boolean
  connectionId: string
  type: ActionTypes.ToggleFavorite
  userId: string
}

interface IToggleFavoriteSuccessAction {
  isFavorite: boolean
  connectionId: string
  type: ActionTypes.ToggleFavoriteSuccess
  userId: string
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | IArchiveLeadSuccessAction
  | IDeleteConnectionFailureAction
  | IDeleteConnectionRequestAction
  | IDeleteConnectionSuccessAction
  | ILoadConnectionFailureAction
  | ILoadConnectionRequestAction
  | ILoadConnectionSuccessAction
  | IResetConnectionViewAction
  | ISetScopeAction
  | IToggleConfirmDeleteAction
  | IToggleFavoriteSuccessAction

// -------------------------
// ACTION CREATORS
// -------------------------
function deleteConnection(connectionId: string): IDeleteConnectionAction {
  return {
    connectionId,
    type: ActionTypes.DeleteConnection,
  }
}

function deleteConnectionFailure(
  error: string,
): IDeleteConnectionFailureAction {
  return {
    error,
    type: ActionTypes.DeleteConnectionFailure,
  }
}

function deleteConnectionRequest(): IDeleteConnectionRequestAction {
  return {
    type: ActionTypes.DeleteConnectionRequest,
  }
}

function deleteConnectionSuccess(): IDeleteConnectionSuccessAction {
  return {
    type: ActionTypes.DeleteConnectionSuccess,
  }
}

function loadConnection(value: string): ILoadConnectionViewAction {
  return { type: ActionTypes.LoadConnection, value }
}

function loadConnectionFailure(error: string): ILoadConnectionFailureAction {
  return { error, type: ActionTypes.LoadConnectionFailure }
}

function loadConnectionRequest(): ILoadConnectionRequestAction {
  return { type: ActionTypes.LoadConnectionRequest }
}

function loadConnectionSuccess(
  item: Contact,
  earnings: EarningSummaryItem[],
  permissions: EntityPermissions,
): ILoadConnectionSuccessAction {
  return {
    earnings,
    item,
    permissions,
    type: ActionTypes.LoadConnectionSuccess,
  }
}

function resetConnectionView(): IResetConnectionViewAction {
  return { type: ActionTypes.ResetConnectionView }
}

function setScope(scope: ConnectionViewScope): ISetScopeAction {
  return {
    scope,
    type: ActionTypes.SetScope,
  }
}

function toggleConfirmDelete(show: boolean): IToggleConfirmDeleteAction {
  return {
    show,
    type: ActionTypes.ToggleConfirmDelete,
  }
}

function toggleFavorite(
  connectionId: string,
  userId: string,
  isFavorite: boolean,
): IToggleFavoriteAction {
  return {
    connectionId,
    isFavorite,
    type: ActionTypes.ToggleFavorite,
    userId,
  }
}

function toggleFavoriteSuccess(
  connectionId: string,
  userId: string,
  isFavorite: boolean,
): IToggleFavoriteSuccessAction {
  return {
    connectionId,
    isFavorite,
    type: ActionTypes.ToggleFavoriteSuccess,
    userId,
  }
}

export const actionCreators = {
  deleteConnection,
  loadConnection,
  resetConnectionView,
  setScope,
  toggleConfirmDelete,
  toggleFavorite,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<ConnectionViewState> = (
  state: ConnectionViewState = new ConnectionViewState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case FeedItemActionTypes.ArchiveLeadSuccess:
      const leadIndex = state.item.leads.findIndex((item: any) => {
        return item.id === action.id
      })

      if (leadIndex > -1) {
        return state.setIn(
          ['item', 'leads', leadIndex, 'isArchived'],
          true,
        ) as ConnectionViewState
      }

      return state

    case ActionTypes.DeleteConnectionFailure:
      return state
        .set('error', action.error)
        .set('isDeleting', false) as ConnectionViewState

    case ActionTypes.DeleteConnectionRequest:
      return state.set('isDeleting', true) as ConnectionViewState

    case ActionTypes.DeleteConnectionSuccess:
      return state
        .set('isDeleting', false)
        .set('item', new Contact()) as ConnectionViewState

    case ActionTypes.LoadConnectionFailure:
      return state
        .set('isLoading', false)
        .set('error', action.error) as ConnectionViewState

    case ActionTypes.LoadConnectionRequest:
      return state.set('isLoading', true) as ConnectionViewState

    case ActionTypes.LoadConnectionSuccess:
      return state
        .set('isLoading', false)
        .set('earnings', action.earnings)
        .set('item', action.item)
        .set('permissions', action.permissions) as ConnectionViewState

    case ActionTypes.ResetConnectionView:
      return state.set('isLoading', false) as ConnectionViewState

    case ActionTypes.SetScope:
      return state.set('scope', action.scope) as ConnectionViewState

    case ActionTypes.ToggleConfirmDelete:
      return state.set('showConfirmDelete', action.show) as ConnectionViewState

    case ActionTypes.ToggleFavoriteSuccess:
      if (state.item.id === action.connectionId) {
        let favorites = state.item.hasStarred
        if (action.isFavorite === false) {
          favorites = favorites.filter((f) => f !== action.userId).toList()
        } else if (!favorites.includes(action.userId)) {
          favorites = favorites.push(action.userId)
        }
        return state.setIn(
          ['item', 'hasStarred'],
          favorites,
        ) as ConnectionViewState
      } else {
        return state
      }

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* deleteConnectionSaga(action: IDeleteConnectionAction) {
  try {
    yield put(deleteConnectionRequest())

    const result = yield makeAuthenticatedRequest(
      `/api/Contacts/${action.connectionId}`,
      { method: RequestMethod.DELETE },
    )

    if (result.success === true) {
      yield put(push('/connections'))
      yield put(deleteConnectionSuccess())
    } else {
      yield put(deleteConnectionFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(deleteConnectionFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* loadConnectionSaga(action: ILoadConnectionViewAction) {
  try {
    yield put(loadConnectionRequest())

    let baseUrl = `/api/Contacts/${action.value}`

    const result = yield makeAuthenticatedRequest(baseUrl)

    if (result.success === true) {
      const entity = result.entity as Contact

      baseUrl = `/api/BrokeragePipelines/${entity.id}/Earnings`

      const earningsResult = yield makeAuthenticatedRequest(baseUrl)
      const earnings: EarningSummaryItem[] = earningsResult.results.map(
        (c: any) => EarningSummaryItem.fromJS(c),
      )

      yield put(
        loadConnectionSuccess(
          Contact.fromJS(entity),
          earnings,
          EntityPermissions.fromJS(result.permissions),
        ),
      )
    } else {
      yield put(loadConnectionFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(errorToast('Oops!', err.message))
  }
}

function* toggleFavoriteSaga(action: IToggleFavoriteAction) {
  try {
    const url = `/api/Contacts/my/${action.connectionId}`

    const opts: IRequestOptions = {
      method: action.isFavorite ? RequestMethod.POST : RequestMethod.DELETE,
    }

    const result = yield makeAuthenticatedRequest(url, opts)

    if (result.success === true) {
      yield put(
        toggleFavoriteSuccess(
          action.connectionId,
          action.userId,
          action.isFavorite,
        ),
      )
    } else {
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchDeleteConnection() {
  yield takeLatest(ActionTypes.DeleteConnection, deleteConnectionSaga)
}

function* watchLoadConnection() {
  yield takeLatest(ActionTypes.LoadConnection, loadConnectionSaga)
}

function* watchToggleFavorite() {
  yield takeLatest(ActionTypes.ToggleFavorite, toggleFavoriteSaga)
}

export function* connectionViewRootSaga() {
  yield fork(watchDeleteConnection)
  yield fork(watchLoadConnection)
  yield fork(watchToggleFavorite)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (
  state: ApplicationState,
): ConnectionViewState => state.connectionView

export const selectEarnings = () =>
  createSelector(selectModuleState(), (mState) => mState.earnings)

export const selectError = () =>
  createSelector(selectModuleState(), (mState) => mState.error)

export const selectIsDeleting = () =>
  createSelector(selectModuleState(), (mState) => mState.isDeleting)

export const selectIsLoading = () =>
  createSelector(selectModuleState(), (mState) => mState.isLoading)

export const selectItem = () =>
  createSelector(selectModuleState(), (mState) => mState.item)

export const selectPermissions = () =>
  createSelector(selectModuleState(), (mState) => mState.permissions)

export const selectScope = () =>
  createSelector(selectModuleState(), (mState) => mState.scope)

export const selectShowConfirmDelete = () =>
  createSelector(selectModuleState(), (mState) => mState.showConfirmDelete)
